import AssistantIcon from "@mui/icons-material/Assistant";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import { GridColDef } from "@mui/x-data-grid";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import NewButton from "../../components/Buttons/NewButton";
import SearchButton from "../../components/Buttons/SearchButton";
import CommonPagination from "../../components/CustomPagination/CommonPagination";
import CommonTable from "../../components/CustomTable/CommonTable";
import { Title } from "../../components/Desc";
import GeneralFailure from "../../components/GeneralFailure";
import ProgressIndicator from "../../components/ProgressIndicator";
import BasicMenu from "../../components/menu/BasicMenu";
import strings from "../../config/localization";
import { useGetAllUsersQuery } from "../../services/appln";
import { getRowsPerPage } from "../constants";
import { doesPermissionExist } from "../util";
import EditUserDialog from "./EditUserDialog";
import ResetPwd from "./ResetPwd";
import UserDelete from "./UserDelete";

export default function ListUsers() {
  const navigate = useNavigate();

  const [pageNo, setPageNo] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(getRowsPerPage());

  const [searchTerm, setSearchTerm] = React.useState("");

  const getParams = {
    page: pageNo,
    limit: rowsPerPage,
    search: searchTerm,
  };

  const { data, error, isLoading, isFetching } = useGetAllUsersQuery(getParams);

  if (error) {
    return <GeneralFailure message={strings.failedToLoadUsers} />;
  }

  const columns: GridColDef[] = [
    {
      field: "fullName",
      headerName: strings.name,
      width: 200,
    },
    {
      field: "userName",
      headerName: strings.username,
      width: 200,
    },
    {
      field: "organizationName",
      headerName: strings.organization,
      width: 200,
    },
    {
      field: "roles",
      headerName: strings.roles,
      width: 200,
      renderCell: (params: any) => {
        return params.row.roles
          .map((role: any) => {
            return role.name;
          })
          .join(", ");
      },
    },
  ];

  if (doesPermissionExist("update-user")) {
    columns.push({
      field: "edit",
      width: 150,
      headerName: "",
      renderCell: (params: any) => (
        <EditUserDialog
          title={strings.editUser}
          userId={params.id}
          initialValues={params}
        />
      ),
    });
  }

  if (doesPermissionExist("reset-password")) {
    columns.push({
      field: "resetPwd",
      width: 200,
      headerName: "",
      renderCell: (params: any) => (
        <ResetPwd
          title={strings.resetPasswd}
          userId={params.id}
          initialValues={params}
        />
      ),
    });
  }

  if (doesPermissionExist("delete-user")) {
    columns.push({
      field: "delete",
      headerName: "",
      width: 150,
      renderCell: (params: any) => <UserDelete userId={params.id} />,
    });
  }

  if (doesPermissionExist("read-role")) {
    columns.push({
      field: "menu",
      headerName: "",
      width: 150,
      renderCell: (params: any) => {
        const options = [
          [
            strings.roles,
            () => navigate(`../roles/${params.id}`),
            <AssistantIcon />,
          ],
        ];
        return <BasicMenu action={strings.actions} options={options} />;
      },
    });
  }

  const handleChangePage = (event: any, value: any) => {
    setPageNo(value);
  };

  const handleChangeCount = (e: any) => {
    setRowsPerPage(e.target.value);
  };

  return (
    <div>
      <Box sx={{ height: 400, width: "100%" }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Title text={strings.users} />
          </Grid>
          {(isLoading || isFetching) && <ProgressIndicator />}
          {doesPermissionExist("create-user") ? (
            <>
              <Grid item xs={2}>
                <NewButton
                  onClickHandler={() => {
                    navigate("../newUser");
                  }}
                />
              </Grid>
              <Grid item xs={10}>
                <SearchButton
                  onClickHandler={(searchTerm: string) => {
                    setSearchTerm(searchTerm);
                  }}
                />
              </Grid>
            </>
          ) : (
            <Grid item xs={12}>
              <SearchButton
                onClickHandler={(searchTerm: string) => {
                  setSearchTerm(searchTerm);
                }}
              />
            </Grid>
          )}
          <Grid item xs={12}>
            <div>
              <CommonTable rows={data ? data?.data : []} columns={columns} />
              <CommonPagination
                count={Math.ceil(data?.totalCount / rowsPerPage)}
                page={pageNo}
                handleChangePage={handleChangePage}
                rowPerPage={rowsPerPage}
                handleChangeCount={handleChangeCount}
              />
            </div>
          </Grid>
        </Grid>
      </Box>
    </div>
  );
}
